import type { Game, PLTableEntry } from './types'
import type { TeamId } from './teams'

/** `PLTableEntry` carries the full stat line, so the standings row and the display row are the
 *  same shape. Kept as an alias for the callers that name it. */
export type PLStandingsEntry = PLTableEntry

// No display-name remapping here: fixtures carry canonical team ids, so the table is keyed by
// identity and the display name is resolved once, at render, via `teamDisplayName`.

type TeamStats = Omit<PLStandingsEntry, 'position' | 'team'>

function emptyStats(): TeamStats {
  return {
    points: 0,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDifference: 0,
  }
}

/**
 * Derives PL standings from the simulated season's fixtures + results, up to
 * (and including) `uptoGameweek`. Callers control how "played so far" is
 * defined for the current gameweek by what they pass in `resultsByGameweek`
 * for that entry (e.g. only reveal-gated results), while earlier gameweeks
 * are expected to be fully resolved.
 */
export function computePLTable(
  gamesByGameweek: Record<number, Game[]>,
  resultsByGameweek: Record<number, Record<string, { homeScore: number; awayScore: number }>>,
  uptoGameweek: number
): PLStandingsEntry[] {
  const stats = new Map<TeamId, TeamStats>()

  const ensure = (team: TeamId): TeamStats => {
    let entry = stats.get(team)
    if (!entry) {
      entry = emptyStats()
      stats.set(team, entry)
    }
    return entry
  }

  // Seed every team that appears anywhere in the season so clubs with no
  // played games yet still show up with a 0/0/0 row instead of vanishing.
  for (const games of Object.values(gamesByGameweek)) {
    for (const game of games) {
      ensure(game.homeTeam)
      ensure(game.awayTeam)
    }
  }

  for (let gw = 1; gw <= uptoGameweek; gw++) {
    const games = gamesByGameweek[gw] ?? []
    const results = resultsByGameweek[gw] ?? {}

    for (const game of games) {
      const result = results[game.id]
      if (!result) continue

      const home = ensure(game.homeTeam)
      const away = ensure(game.awayTeam)
      const { homeScore, awayScore } = result

      home.played += 1
      away.played += 1
      home.goalsFor += homeScore
      home.goalsAgainst += awayScore
      away.goalsFor += awayScore
      away.goalsAgainst += homeScore

      if (homeScore > awayScore) {
        home.points += 3
        home.won += 1
        away.lost += 1
      } else if (awayScore > homeScore) {
        away.points += 3
        away.won += 1
        home.lost += 1
      } else {
        home.points += 1
        away.points += 1
        home.drawn += 1
        away.drawn += 1
      }
    }
  }

  for (const entry of stats.values()) {
    entry.goalDifference = entry.goalsFor - entry.goalsAgainst
  }

  const sorted = [...stats.entries()].sort(([teamA, a], [teamB, b]) => {
    if (b.points !== a.points) return b.points - a.points
    if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference
    if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor
    return teamA.localeCompare(teamB)
  })

  return sorted.map(([team, entryStats], index) => ({
    position: index + 1,
    team,
    ...entryStats,
  }))
}
